import React, { FC, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { Stakeholder as StakeholderHook, useReactForm } from '../../../../hooks/index';
import { PageRoute } from '../../../../types/page.types';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { ModifyGuideTab } from '../../../modify-guide/modify-guide.types';
import { hasPartiallyTerminated } from './partial-termination.util';
import { TerminateForm } from './terminate-form';
import { TerminateStakeholderModalProps } from './terminate-stakeholder-modal';
import { FormSchema, formSchema } from './terminate-stakeholder-modal.validation';

export const EditStakeholderTerminationModal: FC<TerminateStakeholderModalProps> = ({
  isOpen,
  companyId,
  handleClose,
  invalidateQuery,
  stakeholders,
  setSelectedStakeholderIds,
}) => {
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getFieldState,
    resetField,
    clearErrors,
    setError,
  } = useReactForm({
    schema: formSchema,
  });

  const { terminate } = StakeholderHook.useTerminate();

  useEffect(() => {
    if (!stakeholders) return;

    reset({
      terminateForm: {
        stakeholderItems: stakeholders.map(
          ({ id, terminationCause, terminationDate, lastVestingDate }) => ({
            id,
            terminationCause,
            terminationDate: terminationDate ? new Date(terminationDate) : undefined,
            lastVestingDate: lastVestingDate ? new Date(lastVestingDate) : undefined,
          }),
        ),
      },
    });
  }, [reset, stakeholders]);

  const handleCloseModal = useCallback(() => {
    reset();
    handleClose();
    setSelectedStakeholderIds([]);
  }, [reset, handleClose, setSelectedStakeholderIds]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { terminateForm } = data;
      const stakeholders = terminateForm.stakeholderItems.map(
        ({ id, terminationCause, terminationDate, grantItems, lastVestingDate }) => ({
          id,
          terminationDate: toBackendDateFormat(terminationDate),
          terminationCause,
          lastVestingDate: lastVestingDate
            ? toBackendDateFormat(lastVestingDate)
            : toBackendDateFormat(terminationDate),
          grantItems:
            grantItems?.map(
              ({ id, finalExerciseDate, grantItem, vestedSharesCount, exercisedCount }) => ({
                id,
                finalExerciseDate:
                  finalExerciseDate?.getTime() === 0
                    ? undefined
                    : finalExerciseDate
                      ? toBackendDateFormat(finalExerciseDate)
                      : undefined,
                planType: grantItem.plan.type,
                numbersOfGrants: grantItem?.numbersOfGrants || null,
                vestedSharesCount: vestedSharesCount || null,
                unvestedSharesCount: grantItem.numbersOfGrants
                  ? grantItem.numbersOfGrants - (vestedSharesCount || 0)
                  : null,
                exercisedCount: exercisedCount || null,
                unexercisedSharesCount: vestedSharesCount
                  ? vestedSharesCount - (exercisedCount || 0)
                  : null,
              }),
            ) || undefined,
        }),
      );

      terminate(
        {
          companyId,
          data: { stakeholders, sendEmails: !!terminateForm.isSendEmails },
        },
        {
          onSuccess: () => {
            toast.success(
              `Termination policy updated for ${terminateForm.stakeholderItems.length} stakeholder${terminateForm.stakeholderItems.length > 1 ? 's' : ''}`,
            );
            invalidateQuery();
            handleCloseModal();
          },
          // Same batch endpoint as the terminate modal, so the same partial commit is possible: a
          // payload rejected partway through leaves the earlier stakeholders' policies already
          // rewritten. Refetch either way, and close on a partial batch so the retry is re-picked
          // from a refreshed list rather than resubmitted from a form that no longer matches.
          onError: (error) => {
            invalidateQuery();

            if (hasPartiallyTerminated(error)) handleCloseModal();
          },
        },
      );
    },
    [companyId, handleCloseModal, invalidateQuery, terminate],
  );

  const handleTerminate = useCallback(() => {
    handleSubmit(submitHandler, handleFormInvalid)();
  }, [handleSubmit, submitHandler]);

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpen}>
      <SheetContent
        className="h-fit max-h-[800px] w-[90%] max-w-[1400px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="CENTER"
      >
        <div className="flex h-fit w-full flex-col gap-10 rounded-2xl bg-white p-8">
          <div className="flex h-fit w-full items-end justify-between">
            <div className="flex flex-col gap-2">
              <span className="text-xl font-semibold text-gray-700">
                <AppFormattedMessage id="EDIT_TERMINATION" />
              </span>
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="FILL_IN_DETAILS" />
              </span>
            </div>
            <Link
              className="flex w-fit cursor-pointer items-center justify-center gap-2 rounded text-sm font-[450] text-brand-700 underline disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-300 disabled:ring-1 disabled:ring-gray-200"
              rel="noopener noreferrer"
              target="_blank"
              to={`${PageRoute.GUIDES}/${ModifyGuideTab.STAKEHOLDERS_TERMINATION}`}
            >
              <AppFormattedMessage id="LEARN_MORE_TERMINATION" />
            </Link>
          </div>
          <div className="flex h-full max-h-[600px] flex-col overflow-y-auto">
            <TerminateForm
              clearErrors={clearErrors}
              companyId={companyId}
              control={control}
              editMode
              filedState={getFieldState}
              handleCloseModal={handleCloseModal}
              handleTerminate={handleTerminate}
              resetField={resetField}
              setError={setError}
              setFormData={setValue}
              stakeholders={stakeholders}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
